import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatAction, FollowUp, Message, Suggestion } from '../types';
import { chatApi } from '../lib/api';
import {
  getVisitorId, getConversationUuid, setConversationUuid, clearConversation, clearAll,
  loadMessages, saveMessages, touchActivity, lastActivityAt,
} from '../lib/visitor';
import { Composer } from './Composer';
import { renderMarkdown } from '../lib/markdown';

const INACTIVITY_WARN_MS = 5 * 60 * 1000;  // 5 minutes
const INACTIVITY_END_MS  = 7 * 60 * 1000;  // 7 minutes

interface PanelProps {
  onClose: () => void;
}

export function Panel({ onClose }: PanelProps) {
  const cfg = window.grippyWidget;
  // Seed messages from localStorage so the conversation persists across
  // page navigations and panel close/reopen.
  const [messages, setMessages] = useState<Message[]>(() => loadMessages());
  // Seed suggestions from grippyWidget config so they show immediately
  // on open — even for returning visitors who already have a conversation
  // and won't trigger /chat/start.
  const [suggestions, setSuggestions] = useState<Suggestion[]>(cfg.persona.suggestions ?? []);
  const [conversationUuid, setUuid] = useState<string | null>(getConversationUuid());
  const [sending, setSending] = useState(false);
  const [personaName, setPersonaName] = useState(cfg.persona.name);
  const [greeting, setGreeting] = useState(cfg.persona.greeting);
  const listRef = useRef<HTMLDivElement>(null);

  // Bootstrap a conversation on first open.
  useEffect(() => {
    let cancelled = false;
    if (conversationUuid) return;
    chatApi
      .start(getVisitorId(), cfg.pageUrl, cfg.locale)
      .then((r) => {
        if (cancelled) return;
        setUuid(r.conversation_uuid);
        setConversationUuid(r.conversation_uuid);
        setGreeting(r.greeting || cfg.persona.greeting);
        setPersonaName(r.persona_name || cfg.persona.name);
        setSuggestions(r.suggestions ?? []);
      })
      .catch(() => {
        // Silent failure; user can still close + reopen later.
      });
    return () => {
      cancelled = true;
    };
  }, [conversationUuid, cfg]);

  // Auto-scroll on new message.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  // Persist messages to localStorage on every change so the conversation
  // survives page navigations + panel close/reopen.
  useEffect(() => {
    saveMessages(messages);
    if (messages.length > 0) touchActivity();
  }, [messages]);

  // Inactivity timers: at 5 min, bot asks "still there?". At 7 min, the
  // conversation auto-ends and clears local state for a fresh start.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (messages.length === 0) return;
    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityAt();
      if (idle >= INACTIVITY_END_MS) {
        setMessages((m) => [
          ...m,
          { id: cryptoId(), role: 'assistant', content: 'Ending this chat for now — come back any time!' },
        ]);
        setTimeout(() => {
          clearAll();
          setMessages([]);
          setUuid(null);
          warnedRef.current = false;
        }, 2500);
        clearInterval(interval);
      } else if (idle >= INACTIVITY_WARN_MS && !warnedRef.current) {
        warnedRef.current = true;
        setMessages((m) => [
          ...m,
          { id: cryptoId(), role: 'assistant', content: 'Still there? I\'ll wrap up the chat in 2 minutes if I don\'t hear back — but you can always pick up where we left off.' },
        ]);
      }
    }, 20_000);
    return () => clearInterval(interval);
  }, [messages.length]);

  // iOS keyboard handling: when the soft keyboard appears, visualViewport
  // shrinks AND can be offset from the layout viewport. We need to pin both
  // height AND vertical position so the panel exactly covers the visible
  // area above the keyboard — otherwise the underlying page leaks through
  // in the gap between the composer and the keyboard.
  //
  // Also lock body scroll while the panel is open so iOS doesn't try to
  // auto-scroll the page when focus enters the textarea.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const isMobile = () => window.matchMedia('(max-width: 540px)').matches;

    const prevBodyOverflow = document.body.style.overflow;
    if (isMobile()) {
      document.body.style.overflow = 'hidden';
    }

    const sync = () => {
      const panel = document.querySelector('.g-panel') as HTMLElement | null;
      if (!panel) return;
      if (isMobile()) {
        // Cover exactly the visible viewport: top = offsetTop (handles iOS
        // pinch-zoom / keyboard offset), height = vv.height (above keyboard).
        panel.style.top = `${vv.offsetTop}px`;
        panel.style.height = `${vv.height}px`;
        // Kill the safe-area bottom padding when the keyboard is open —
        // the keyboard already occupies that area so the padding becomes
        // visible dead space.
        const keyboardOpen = vv.height < window.innerHeight - 80;
        panel.style.paddingBottom = keyboardOpen ? '0px' : '';
      } else {
        panel.style.removeProperty('top');
        panel.style.removeProperty('height');
        panel.style.removeProperty('padding-bottom');
      }
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const send = useCallback(
    async (text: string) => {
      if (!conversationUuid) return;
      const userMsg: Message = { id: cryptoId(), role: 'user', content: text };
      setMessages((m) => [...m, userMsg]);
      setSending(true);

      const appendReply = (reply: { reply: string; citations: { title: string; url: string | null }[]; actions?: ChatAction[] }) => {
        setMessages((m) => [
          ...m,
          { id: cryptoId(), role: 'assistant', content: reply.reply, citations: reply.citations, actions: reply.actions },
        ]);
      };

      const appendFallback = () => {
        setMessages((m) => [
          ...m,
          {
            id: cryptoId(),
            role: 'assistant',
            content: "Thanks for your message! I want to make sure I give you the right answer here — could you leave your email or phone number so someone from our team can follow up?",
          },
        ]);
      };

      try {
        const res = await chatApi.send(conversationUuid, getVisitorId(), text);
        appendReply(res);
      } catch {
        // Most common cause of a failed send is a stale conversation_uuid
        // in localStorage (orphaned from a previous install where tables
        // didn't yet exist). Self-heal: clear, start a fresh conversation,
        // and retry the message once with the new UUID.
        try {
          clearConversation();
          const fresh = await chatApi.start(getVisitorId(), cfg.pageUrl, cfg.locale);
          setConversationUuid(fresh.conversation_uuid);
          setUuid(fresh.conversation_uuid);
          const retried = await chatApi.send(fresh.conversation_uuid, getVisitorId(), text);
          appendReply(retried);
        } catch {
          appendFallback();
        }
      } finally {
        setSending(false);
      }
    },
    [conversationUuid, cfg],
  );

  // Hybrid flow: if a topic has a scripted reply, show it inline without
  // hitting the AI, then render 3 inline buttons under the reply so the
  // visitor can visually continue the conversation (problem solved? need
  // more detail? want a human?) without typing.
  const handleTopicClick = useCallback(
    (s: Suggestion) => {
      if (s.reply && s.reply.trim() !== '') {
        const followUps: FollowUp[] = [
          { label: '✓ Yes, that helped', action: 'helped' },
          { label: 'Tell me more', action: 'deeper', topic: s.label },
          { label: 'Talk to a human', action: 'human' },
        ];
        setMessages((m) => [
          ...m,
          { id: cryptoId(), role: 'user', content: s.label },
          { id: cryptoId(), role: 'assistant', content: s.reply!.trim(), followUps },
        ]);
        return;
      }
      send(s.label);
    },
    [send],
  );

  const handleAction = useCallback(
    (msgId: string, action: ChatAction) => {
      // Remove actions from this message so buttons can't be clicked twice.
      setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, actions: undefined } : x)));
      if (action.url) {
        window.open(action.url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (action.prompt) {
        send(action.prompt);
      }
    },
    [send],
  );

  const handleFollowUp = useCallback(
    (msgId: string, fu: FollowUp) => {
      // Remove the followUps from the message so the buttons disappear once clicked.
      setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, followUps: undefined } : x)));

      if (fu.action === 'helped') {
        setMessages((m) => [
          ...m,
          { id: cryptoId(), role: 'user', content: fu.label },
          { id: cryptoId(), role: 'assistant', content: "Glad I could help! Anything else you'd like to know? Browse the topics below or just ask." },
        ]);
        return;
      }
      if (fu.action === 'human') {
        setMessages((m) => [
          ...m,
          { id: cryptoId(), role: 'user', content: fu.label },
          { id: cryptoId(), role: 'assistant', content: "Of course — please share your email or phone number and someone from our team will follow up shortly." },
        ]);
        return;
      }
      // 'deeper' → ask the LLM to expand on the topic.
      send(`Can you tell me more about: ${fu.topic ?? fu.label}? Please go into more detail.`);
    },
    [send],
  );

  const initial = personaName?.charAt(0)?.toUpperCase() ?? 'A';
  const hasMessages = messages.length > 0;

  // Latest assistant actions take precedence over the persistent topic
  // chips — when the LLM has suggested contextual next steps (e.g.
  // "View dieticians", "Talk to a human") they replace the static
  // "What services do you offer?" chips so the visitor sees the most
  // relevant CTAs for the moment they're in.
  const latestAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const latestActions = latestAssistant?.actions && latestAssistant.actions.length > 0
    ? { msgId: latestAssistant.id, actions: latestAssistant.actions }
    : null;

  return (
    <div className="g-panel" role="dialog" aria-label="Chat">
      <header className="g-header" data-thinking={sending}>
        <div className="g-header__avatar" aria-hidden="true">
          {initial}
        </div>
        <div>
          <div className="g-header__name">{personaName}</div>
          <div className="g-header__sub">{cfg.siteName}</div>
        </div>
        <button type="button" className="g-header__close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="g-messages" ref={listRef}>
        {!hasMessages && (
          <>
            {/* Greeting appears as the assistant's first real message */}
            <div className="g-msg g-msg--assistant g-msg--greeting">
              {renderMarkdown(greeting)}
            </div>
            {suggestions.length > 0 && (
              <>
                <div className="g-prompts-label">Topics I can help with</div>
                <div className="g-prompts">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      className="g-prompt"
                      onClick={() => handleTopicClick(s)}
                      disabled={sending}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {messages.map((msg) =>
          msg.role === 'user' ? (
            <div key={msg.id} className="g-msg g-msg--user">
              {msg.content}
            </div>
          ) : (
            <div key={msg.id} className="g-msg g-msg--assistant">
              {renderMarkdown(msg.content)}
              {msg.followUps && msg.followUps.length > 0 && (
                <div className="g-followups">
                  {msg.followUps.map((fu, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`g-followup g-followup--${fu.action}`}
                      onClick={() => handleFollowUp(msg.id, fu)}
                      disabled={sending}
                    >
                      {fu.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Citations hidden from customer-facing widget on purpose —
                  internal page-builder titles (e.g. "kit-4", "Section 2",
                  "Header Template") leaked through and read as noise to
                  visitors. The data is still stored server-side on the
                  message row for admin analytics. */}
            </div>
          ),
        )}

        {sending && (
          <div className="g-thinking" aria-label="Thinking">
            <span className="g-thinking__dot" />
            <span className="g-thinking__dot" />
            <span className="g-thinking__dot" />
          </div>
        )}
      </div>

      {/* Contextual action chips from the latest AI reply take priority.
          When none exist, fall back to the persistent topic chips. */}
      {hasMessages && latestActions && (
        <div className="g-quick-chips g-quick-chips--actions">
          {latestActions.actions.map((a, i) => (
            <button
              key={i}
              type="button"
              className={`g-quick-chip ${a.url ? 'g-quick-chip--primary' : ''}`}
              onClick={() => handleAction(latestActions.msgId, a)}
              disabled={sending}
            >
              {a.label}
              {a.url && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7M9 7h8v8" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
      {hasMessages && !latestActions && suggestions.length > 0 && (
        <div className="g-quick-chips">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="g-quick-chip"
              onClick={() => handleTopicClick(s)}
              disabled={sending}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <Composer onSend={send} disabled={sending || !conversationUuid} placeholder="Or type your own question..." />

      {cfg.showBranding && (
        <div className="g-footer">
          Powered by <a href="https://grippycode.com" target="_blank" rel="noreferrer">Grippy Code</a>
        </div>
      )}
    </div>
  );
}

function cryptoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}
